import { Component, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { PageEvent } from '@angular/material/paginator';
import moment from 'moment';
import { AccessGroup, AccessGroupSearchEvent } from 'src/app/features/access-groups/access-group.model';
import { Department } from 'src/app/features/department/department.model';
import { Organization } from 'src/app/features/organization/organization-models';
import { ChargingUserCreationAccessTypeEnum } from 'src/app/shared/enums/charging-user-generation-access-type';
import { AccessGroupService } from 'src/app/shared/services/access-group.service';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { ArrayValidators } from 'src/app/shared/validators/array.validator';
import { InputMaskModule } from '@ngneat/input-mask';
import { TopLoaderService } from 'src/app/core/services/top-loader.service';
import { CreateUserWithAppInvitationRequest } from '../../charging-user-create.model';
import { UserInvitationService } from 'src/app/shared/services/user.invitation.service';
import { SnackbarService, FeedbackInfoComponent } from 'ok-angular-shared/components';
import { firstValueFrom } from 'rxjs';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { EnumToArrayPipe } from '../../../../../shared/pipes/enum-to-array.pipe';
import { TranslateChargingUserCreationAccessTypeEnum } from '../../../../../shared/pipes/translate-charging-user-access-type-enum.pipe';
import { CountryCodeSelectorComponent } from 'src/app/shared/components/country-code-selector/country-code-selector.component';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { PhoneNumberInputComponent } from 'src/app/shared/components/phone-number-input/phone-number-input.component';
import { AccessGroupCarType } from 'src/app/shared/enums/access-group-car-type';
import { AdmOrgTypeaheadComponent } from 'src/app/shared/components/adm-org-typeahead/adm-org-typeahead.component';
import { AdmDepTypeaheadComponent } from 'src/app/shared/components/adm-dep-typeahead/adm-dep-typeahead.component';
import { MatDialogTitle, MatDialogContent, MatDialogActions, MatDialogClose } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatOption } from '@angular/material/core';
import { MatRadioGroup, MatRadioButton } from '@angular/material/radio';
import { MatInput } from '@angular/material/input';
import { MatDateRangeInput, MatStartDate, MatEndDate } from '@angular/material/datepicker';
import { MatDatepickerToggle, MatDateRangePicker } from '@angular/material/datepicker';
import { ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe } from '@angular/common';

interface IdTagCreateUserModalWithgInvitationModel {
  organization: FormControl<Organization | null>;
  department: FormControl<Department | null>;
  email: FormControl<string | null>;
  name: FormControl<string | null>;
  accessType: FormControl<ChargingUserCreationAccessTypeEnum | null>;
  alias: FormControl<string | null>;
  phoneNumber: FormControl<string>;
  selectedAccessGroups: FormControl<SelectedAccessGroup[]>;
  timeTo: FormControl<Date | null>;
  timeFrom: FormControl<Date | null>;
  countryCode: FormControl<number>;
  userHasOrganizationCar: FormControl<boolean | null>;
}
export interface SelectedAccessGroup {
  identifier: string;
  name: string;
}
@Component({
  selector: 'adm-create-charging-user-with-app-invitation.modal',
  templateUrl: './create-charging-user-with-app-invitation.modal.component.html',
  styleUrl: './create-charging-user-with-app-invitation.modal.component.scss',
  imports: [
    InputMaskModule,
    EnumToArrayPipe,
    TranslateChargingUserCreationAccessTypeEnum,
    CountryCodeSelectorComponent,
    CustomDropDownArrowDirective,
    PhoneNumberInputComponent,
    AdmOrgTypeaheadComponent,
    AdmDepTypeaheadComponent,
    FeedbackInfoComponent,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatIcon,
    MatButton,
    MatFormField,
    MatLabel,
    MatError,
    MatSelect,
    MatSelectTrigger,
    MatOption,
    MatRadioGroup,
    MatRadioButton,
    MatInput,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatDatepickerToggle,
    MatDateRangePicker,
    ReactiveFormsModule,
    AsyncPipe,
  ],
})
export class CreateChargingUserWithAppInvitationModalComponent implements OnInit {
  data = inject<{
    organization;
  }>(MAT_DIALOG_DATA);
  private chargingUserService = inject(ChargingUserService);
  dialogRef = inject<MatDialogRef<CreateChargingUserWithAppInvitationModalComponent>>(MatDialogRef);
  private accessGroupService = inject(AccessGroupService);
  private loadingService = inject(TopLoaderService);
  private userInvitationService = inject(UserInvitationService);
  private snackbarService = inject(SnackbarService);

  resetOrgTrigger: WritableSignal<{ reset: boolean }> = signal<{ reset: boolean }>({ reset: false });
  resetDepartmentTrigger: WritableSignal<{ reset: boolean }> = signal<{ reset: boolean }>({ reset: false });
  departmentsDisabled = signal<boolean>(true);

  public accessGroups = signal<AccessGroup[]>([]);

  public userAccessType = ChargingUserCreationAccessTypeEnum;
  public accessGroups$ = toObservable(this.accessGroups);
  public minDate: Date = moment().utc().startOf('day').toDate();

  public userFormGroup: FormGroup<IdTagCreateUserModalWithgInvitationModel> = new FormGroup<IdTagCreateUserModalWithgInvitationModel>({
    organization: new FormControl<Organization | null>(null, [Validators.required, this.organizationIdValidator]),
    department: new FormControl<Department | null>({ value: null, disabled: true }, [Validators.required]),
    accessType: new FormControl<ChargingUserCreationAccessTypeEnum>(ChargingUserCreationAccessTypeEnum.Permanent, [Validators.required]),
    email: new FormControl<string | null>(null),
    name: new FormControl<string | null>(null),
    alias: new FormControl<string | null>(null),
    phoneNumber: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    selectedAccessGroups: new FormControl<SelectedAccessGroup[]>(
      { value: [], disabled: true },
      {
        validators: [ArrayValidators.minLength(1)],
        nonNullable: true,
      }
    ),
    timeTo: new FormControl<Date | null>(null, []),
    timeFrom: new FormControl<Date | null>(null, []),
    countryCode: new FormControl<number>(45, { nonNullable: true }),
    userHasOrganizationCar: new FormControl<boolean | null>(false),
  });

  ngOnInit(): void {
    this.userFormGroup.controls.selectedAccessGroups.setValidators(ArrayValidators.minLength(1));
    this.userFormGroup.controls.userHasOrganizationCar.setValidators(Validators.required);
    this.userFormGroup.controls.userHasOrganizationCar.setValue(false);
    this.userFormGroup.controls.selectedAccessGroups.updateValueAndValidity();
    this.userFormGroup.controls.userHasOrganizationCar.updateValueAndValidity();

    this.userFormGroup.controls.userHasOrganizationCar.valueChanges.subscribe(x => {
      if (this.userFormGroup.controls.organization?.value?.id) {
        this.userFormGroup.controls.selectedAccessGroups.setValue([]);
        this.getAccessGroups(this.userFormGroup.controls.organization.value.id);
      }
    });

    this.ClearControlValidator(this.userFormGroup.controls.email);
    this.userFormGroup.controls.name.setValidators([Validators.required]);
    this.userFormGroup.controls.name.updateValueAndValidity();
    this.userFormGroup.controls.phoneNumber.setValidators([Validators.required]);
    this.userFormGroup.controls.phoneNumber.updateValueAndValidity();

    this.userFormGroup.controls.alias.setValidators([Validators.maxLength(100)]);
    this.userFormGroup.controls.alias.updateValueAndValidity();

    this.userFormGroup.controls.selectedAccessGroups.setValidators(ArrayValidators.minLength(1));
    this.userFormGroup.controls.selectedAccessGroups.updateValueAndValidity();

    this.userFormGroup.controls.accessType.setValue(ChargingUserCreationAccessTypeEnum.Permanent);
    this.userFormGroup.controls.accessType.setValidators(Validators.required);
    this.userFormGroup.controls.accessType.updateValueAndValidity();
    this.setupAccessTypeChange();

    if (this.data?.organization) {
      this.userFormGroup.controls.organization.setValue(this.data.organization);
      this.organizationSelected(this.data.organization);
      this.userFormGroup.patchValue({
        organization: this.data.organization,
      });
    }
  }

  departmentSelected(department: Department | null) {
    this.userFormGroup.patchValue({
      department: department,
    });
  }
  compareObjects(ag1: SelectedAccessGroup, ag2: SelectedAccessGroup): boolean {
    return ag1?.identifier === ag2?.identifier;
  }

  public resetDepartment() {
    this.userFormGroup.patchValue({
      department: null,
    });
  }

  public resetOrganization() {
    this.userFormGroup.patchValue({
      organization: null,
      department: null,
      selectedAccessGroups: [],
    });
    this.userFormGroup.controls.selectedAccessGroups.disable();
    this.accessGroups.set([]);
    this.departmentsDisabled.set(true);
  }

  setupAccessTypeChange() {
    this.userFormGroup.controls.accessType.valueChanges.subscribe(val => {
      if (val == ChargingUserCreationAccessTypeEnum.TimeLimit) {
        this.userFormGroup.controls.timeTo.setValue(moment().utc().startOf('day').toDate());
        this.userFormGroup.controls.timeTo.setValidators(Validators.required);
        this.userFormGroup.controls.timeTo.updateValueAndValidity();
        this.userFormGroup.controls.timeFrom.setValue(moment().utc().startOf('day').toDate());
        this.userFormGroup.controls.timeFrom.setValidators(Validators.required);
        this.userFormGroup.controls.timeFrom.updateValueAndValidity();
      }
      if (val == ChargingUserCreationAccessTypeEnum.Permanent) {
        this.userFormGroup.controls.timeTo.setValue(null);
        this.ClearControlValidator(this.userFormGroup.controls.timeTo);
      }
    });
  }

  public organizationSelected(organization: Organization | null) {
    this.userFormGroup.patchValue({
      organization: organization,
      department: null,
    });
    this.userFormGroup.controls.selectedAccessGroups.enable();
    this.resetDepartmentTrigger.set({ reset: true });
    if (organization) {
      this.departmentsDisabled.set(false);
      this.userFormGroup.controls.department.setValue(null);
      this.getAccessGroups(organization.id);
      return;
    } else {
      this.departmentsDisabled.set(true);
    }
  }
  private getAccessGroups(organizationId: number) {
    const searchEvent: AccessGroupSearchEvent = new AccessGroupSearchEvent(new PageEvent());
    const pageEvent = new PageEvent();
    pageEvent.pageSize = 1000;
    searchEvent.paging = pageEvent;
    searchEvent.organizationId = organizationId;
    searchEvent.carType =
      this.userFormGroup.controls.userHasOrganizationCar.value == null
        ? null
        : this.userFormGroup.controls.userHasOrganizationCar.value
          ? AccessGroupCarType.CompanyCar
          : AccessGroupCarType.PrivateCar;
    this.accessGroupService.getAccessGroups(searchEvent).subscribe({
      next: result => {
        this.accessGroups.set(result.items);
        this.userFormGroup.controls.selectedAccessGroups.enable();
      },
    });
  }
  public enableDepartmentDropdown() {
    this.userFormGroup.controls.department.enable();
  }

  public organizationIdValidator(control: AbstractControl): ValidationErrors | null {
    const org = control.value as Organization | null;
    if (!org || !org.id) {
      return { organizationIdRequired: true };
    }
    return null;
  }

  private ClearControlValidator(fc: FormControl<any>) {
    fc.clearValidators();
    fc.updateValueAndValidity();
  }
  save() {
    this.loadingService.setLoading(true);

    const organization = this.userFormGroup.controls.organization.value;
    const department = this.userFormGroup.controls.department.value;
    const phoneNumber = this.userFormGroup.controls.phoneNumber.value;
    const timeTo = this.userFormGroup.controls.timeTo.value;
    const timeFrom = this.userFormGroup.controls.timeFrom.value;
    const countryCode = this.userFormGroup.controls.countryCode.value;
    const isTimeLimited = this.userFormGroup.controls.accessType.value === ChargingUserCreationAccessTypeEnum.TimeLimit;
    if (!organization || !department || !phoneNumber || countryCode == null || (isTimeLimited && (!timeTo || !timeFrom))) {
      this.loadingService.setLoading(false);
      return;
    }

    const accessGroupIdentifiers = this.userFormGroup.controls.selectedAccessGroups.value.flatMap(ag => ag.identifier);
    const model: CreateUserWithAppInvitationRequest = {
      organizationId: organization.id,
      alias: this.userFormGroup.controls.alias.value ?? '',
      name: this.userFormGroup.controls.name.value ?? '',
      phoneNumber,
      departmentId: department.id,
      timeTo: isTimeLimited && timeTo ? moment(timeTo).endOf('day').toDate() : undefined,
      timeFrom: isTimeLimited && timeFrom ? moment(timeFrom).startOf('day').toDate() : undefined,
      accessGroupIdentifiers: accessGroupIdentifiers,
      phoneCountryCode: countryCode,
      carType:
        this.userFormGroup.controls.userHasOrganizationCar.value == null
          ? undefined
          : this.userFormGroup.controls.userHasOrganizationCar.value
            ? AccessGroupCarType.CompanyCar
            : AccessGroupCarType.PrivateCar,
    };

    this.userInvitationService.createUserWithAppInvitation(model).subscribe({
      next: async (chargingUserId: number) => {
        const chargingUser = await firstValueFrom(this.chargingUserService.getChargingUser(chargingUserId));
        this.dialogRef.close(chargingUser);
        this.snackbarService.success(`Bruger oprettet`);
      },
      error: (httpError: HttpErrorResponse) => {
        if (httpError.status == HttpStatusCode.BadRequest) {
          switch (httpError.error?.code) {
            case 101100:
              this.snackbarService.error('Mobilnummeret angivet er ikke et gyldigt telefonnummer for den indtastede landekode.');
              break;
            case 101101:
              this.snackbarService.error('Mobilnummeret er af et ugyldigt format for den indtastede landekode.');
              break;
            default:
              this.snackbarService.error(httpError.error?.message);
              break;
          }
        } else {
          this.snackbarService.error(`Der er sket en ukendt fejl: (${httpError.error.message})`);
        }
      },
    });
  }
}
